import React, { useContext, useState } from 'react';
import { Box, IconButton, TextField, Tooltip, Collapse } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import CloseIcon from '@mui/icons-material/Close';
import SparkButton from './SparkButton';
import { AuthContext, MyContext } from '../context/AuthContext';

/**
 * Inline chain-reply composer for sparks. Renders a small "🔗 chain"
 * button next to a spark post; clicking it expands an inline TextField
 * + SparkButton with `parentId` so the spark RPC handles the +5 minute
 * chain bonus to the parent.
 *
 * Kept tiny because sparks are ultra-shortlived — by the time you've
 * typed a long reply, the parent might already be expired.
 */
function SparkChainReply({ parentSparkId }: { parentSparkId: string }) {
  const userRecord = useContext<MyContext>(AuthContext).userRecord;
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');

  if (!userRecord) return null;

  return (
    <Box sx={{ mt: 1 }}>
      {!open ? (
        <Tooltip title="Reply with a chain spark — both sparks get +5 min">
          <IconButton
            size="small"
            onClick={() => setOpen(true)}
            sx={{
              color: '#ff9800',
              border: '1px dashed rgba(255,152,0,0.4)',
              borderRadius: 1.25,
              px: 0.75,
              py: 0.25,
              fontSize: '0.7rem',
              fontFamily: '"VT323", monospace',
              letterSpacing: 1,
              gap: 0.5,
              '&:hover': { bgcolor: 'rgba(255,152,0,0.08)', borderColor: '#ff9800' },
            }}
          >
            <LinkIcon sx={{ fontSize: 14 }} />
            CHAIN
          </IconButton>
        </Tooltip>
      ) : (
        <Collapse in={open}>
          <Box
            sx={{
              p: 1.25,
              borderRadius: 1.5,
              border: '1px dashed rgba(255,152,0,0.4)',
              bgcolor: 'rgba(255,152,0,0.04)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <TextField
              size="small"
              fullWidth
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value.slice(0, 280))}
              placeholder="Quick spark reply…"
              inputProps={{ maxLength: 280 }}
              sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem' } }}
            />
            <SparkButton
              value={value}
              parentId={parentSparkId}
              onFired={() => { setValue(''); setOpen(false); }}
            />
            <IconButton
              size="small"
              onClick={() => { setOpen(false); setValue(''); }}
              sx={{ color: 'text.disabled' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

export default SparkChainReply;
